// Session analysis for Automower history points
// Translates raw GPS/status events into sessions, timeline and a summary

const STATUS_LABELS = {
  IN_OPERATION: 'En fonctionnement',
  CHARGING: 'En charge',
  PARKED: 'Stationné',
  RESTRICTED: 'Stationné à la base',
  GOING_HOME: 'Retour à la base',
  LEAVING_STATION: 'Départ de la base',
  MAIN_AREA: 'Zone principale',
  WORK_AREA: 'Zone de travail',
  MOWING: 'Tonte',
  NOT_APPLICABLE: 'Activité',
  PAUSED: 'En pause',
  STOPPED: 'Arrêté',
  ERROR: 'Erreur',
  FATAL_ERROR: 'Erreur critique',
  ERROR_AT_POWER_UP: 'Erreur au démarrage',
  STOPPED_IN_GARDEN: 'Arrêté dans le jardin',
  PARKED_IN_CS: 'Stationné à la base',
  UNKNOWN: 'État inconnu',
};

const ACTIVITY_LABELS = {
  MOWING: 'Tonte',
  GOING_HOME: 'Retour base',
  LEAVING: 'Départ',
  CHARGING: 'Charge',
  PARKED: 'Stationné',
  PARKED_IN_CS: 'Stationné à la base',
  NOT_APPLICABLE: '',
  UNKNOWN: '',
};

export const translateStatus = (status, ctx = {}) => {
  if (!status) return '—';
  const { activity, previousStatus, battery } = ctx;
  if (status === 'UNKNOWN' || status === 'NOT_APPLICABLE') {
    if (activity === 'CHARGING') return 'En charge';
    if (activity === 'MOWING') return 'Tonte';
    if (activity === 'LEAVING') return 'Départ';
    if (activity === 'GOING_HOME') return 'Retour';
    if (activity === 'PARKED_IN_CS') return 'Stationné à la base';
    if (previousStatus === 'CHARGING') return 'Initialisation';
    if (previousStatus === 'GOING_HOME') return 'Arrivé à la base';
    if (previousStatus === 'LEAVING_STATION') return 'En déplacement';
    if (battery && battery < 30) return 'Batterie faible';
    if (battery && battery > 95) return 'Batterie pleine';
  }
  return STATUS_LABELS[status] || status.replace(/_/g, ' ');
};

export const translateActivity = (activity, ctx = {}) => {
  if (!activity) return '';
  const { status, isTransition } = ctx;
  if (activity === 'UNKNOWN' || activity === 'NOT_APPLICABLE') {
    if (status === 'RESTRICTED') return 'à la base';
    if (status === 'CHARGING') return 'en charge';
    if (status === 'IN_OPERATION') return 'en fonctionnement';
    if (status === 'LEAVING_STATION') return 'en départ';
    if (isTransition) return 'transition';
    return '';
  }
  return ACTIVITY_LABELS[activity] || activity.replace(/_/g, ' ');
};

const determineSessionType = (status, activity) => {
  const s = (status || '').toLowerCase();
  const a = (activity || '').toLowerCase();
  if (s.includes('mowing') || a.includes('mowing')) return 'mowing';
  if (s.includes('charging') || a.includes('charging')) return 'charging';
  if (s.includes('going_home') || s.includes('leaving')) return 'moving';
  if (s.includes('parked') || s.includes('stopped')) return 'parked';
  return 'other';
};

const sessionTypeLabel = (type, status, activity, workArea) => {
  switch (type) {
    case 'mowing': return workArea ? `Tonte ${workArea}` : 'Tonte';
    case 'charging': return 'Session de charge';
    case 'moving':
      if ((status || '').includes('GOING_HOME') || (activity || '').includes('GOING_HOME')) return 'Retour à la base';
      if ((status || '').includes('LEAVING') || (activity || '').includes('LEAVING')) return 'Départ de la base';
      return 'Déplacement';
    case 'parked':
      if (status === 'RESTRICTED') return 'Stationné à la base';
      return 'En pause';
    default: {
      if (status === 'UNKNOWN' && (activity === 'NOT_APPLICABLE' || !activity)) return 'Initialisation';
      if (status === 'UNKNOWN') return translateStatus(status, { activity });
      if (status === 'RESTRICTED') return 'Stationné à la base';
      return translateStatus(status, { activity }) || 'Activité';
    }
  }
};

const fmtDuration = (minutes) => {
  const m = Math.max(0, Math.floor(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h > 0) return r > 0 ? `${h}h${String(r).padStart(2, '0')}` : `${h}h`;
  return `${m}m`;
};

const fmtTime = (d) => {
  if (!d) return '—';
  const date = d?.getTime ? d : new Date(d);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export const analyzeHistorySessions = (points) => {
  if (!points || !Array.isArray(points) || points.length === 0) {
    return {
      sessions: [],
      timeline: [],
      summary: {
        totalSessions: 0,
        totalMowingTime: 0,
        totalChargingTime: 0,
        zonesWorked: [],
        totalPoints: 0,
      },
    };
  }

  const sorted = [...points].sort((a, b) => {
    const ta = a.timestamp?.getTime ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
    const tb = b.timestamp?.getTime ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
    return ta - tb;
  });

  const sessions = [];
  const timeline = [];
  let current = null;
  let prev = null;

  sorted.forEach((point, i) => {
    const ts = point.timestamp?.getTime ? point.timestamp : new Date(point.timestamp);
    const status = point.status || 'UNKNOWN';
    const activity = point.activity || status;
    const workArea = point.workAreaName || point.currentWorkAreaName || null;
    const workAreaId = point.workAreaId || point.currentWorkAreaId || null;
    const battery = point.battery ?? point.batteryPercent ?? null;

    const statusChanged = prev && prev.status !== status;
    const activityChanged = prev && prev.activity !== activity;
    const zoneChanged = prev && (prev.workAreaId !== workAreaId || prev.workAreaName !== workArea);
    const change = !prev || statusChanged || activityChanged || zoneChanged;

    if (change) {
      if (current) {
        const endTime = prev.timestamp?.getTime ? prev.timestamp : new Date(prev.timestamp);
        current.endTime = endTime;
        current.endTimeFormatted = fmtTime(endTime);
        current.duration = (endTime - current.startTime) / (1000 * 60);
        current.formattedDuration = fmtDuration(current.duration);
        sessions.push(current);
      }

      const type = determineSessionType(status, activity);
      const description = sessionTypeLabel(type, status, activity, workArea);

      current = {
        id: `s${i}`,
        startTime: ts,
        startTimeFormatted: fmtTime(ts),
        endTime: ts,
        endTimeFormatted: fmtTime(ts),
        duration: 0,
        formattedDuration: '0m',
        status,
        activity,
        workArea,
        workAreaId,
        battery,
        type,
        description,
        changes: [],
      };

      if (statusChanged) current.changes.push({ k: 'status', from: prev.status, to: status });
      if (activityChanged) current.changes.push({ k: 'activity', from: prev.activity, to: activity });
      if (zoneChanged) current.changes.push({ k: 'zone', from: prev.workAreaName, to: workArea });

      timeline.push({
        time: fmtTime(ts),
        timestamp: ts,
        type,
        description,
        status,
        activity,
        workArea,
        battery,
        changes: current.changes,
      });
    }
    prev = { ...point, status, activity, workArea, workAreaId };
  });

  if (current && sorted.length > 0) {
    const last = sorted[sorted.length - 1];
    const endTime = last.timestamp?.getTime ? last.timestamp : new Date(last.timestamp);
    current.endTime = endTime;
    current.endTimeFormatted = fmtTime(endTime);
    current.duration = (endTime - current.startTime) / (1000 * 60);
    current.formattedDuration = fmtDuration(current.duration);
    sessions.push(current);
  }

  const summary = {
    totalSessions: sessions.length,
    totalMowingTime: sessions.filter(s => s.type === 'mowing').reduce((a, s) => a + s.duration, 0),
    totalChargingTime: sessions.filter(s => s.type === 'charging').reduce((a, s) => a + s.duration, 0),
    zonesWorked: [...new Set(sessions.filter(s => s.type === 'mowing' && s.workArea).map(s => s.workArea))],
    totalPoints: sorted.length,
  };

  timeline.sort((a, b) => b.timestamp - a.timestamp);
  sessions.sort((a, b) => b.startTime - a.startTime);

  return { sessions, timeline, summary, fmtDuration };
};

export { fmtDuration, fmtTime };
